import { useCallback, useEffect, useState } from "react";
import { AuthStatusOptionModel } from "../../../../../data-model/authStatusOptions";
import { OrderAssignmentConfiguration } from "../../../../../../shared/api/services/orderAssignmentConfigService";
import {
  comparePayerPatterns,
  createToggleHandler,
} from "../utils/orderAssignmentHelpers";

interface UseConfigurationSelectionsReturn {
  selectedAuthStatuses: string[];
  selectedPayerPatterns: string[];
  handleAuthStatusToggle: (statusId: string) => void;
  handlePayerPatternToggle: (pattern: string) => void;
  handleSelectAllAuthStatuses: (allOptions: string[]) => void;
  handleClearAllAuthStatuses: () => void;
  handleSelectAllPayerPatterns: (allOptions: string[]) => void;
  handleClearAllPayerPatterns: () => void;
  resetSelections: () => void;
}

/**
 * useConfigurationSelections
 *
 * Custom hook that manages selected auth statuses and payer patterns for order assignment configuration.
 * Handles initialization from config, normalization of payer patterns, and provides toggle/reset handlers.
 *
 * @param {OrderAssignmentConfiguration | null} config - The configuration object containing initial values
 * @param {boolean} isOpen - Whether the modal/dialog is open (controls when to initialize)
 * @param {string[]} payerPatternOptions - Available payer pattern options (used for normalization)
 * @param {AuthStatusOptionModel[]} authStatusOptions - Available auth status options (used for "any" handling)
 * @returns {UseConfigurationSelectionsReturn} Return object containing:
 * @returns {string[]} return.selectedAuthStatuses - Currently selected auth status IDs
 * @returns {string[]} return.selectedPayerPatterns - Currently selected payer patterns
 * @returns {(statusId: string) => void} return.handleAuthStatusToggle - Function to toggle an auth status selection
 * @returns {(pattern: string) => void} return.handlePayerPatternToggle - Function to toggle a payer pattern selection
 * @returns {() => void} return.resetSelections - Function to reset selections to original config values
 */
export const useConfigurationSelections = (
  config: OrderAssignmentConfiguration | null,
  isOpen: boolean,
  payerPatternOptions: string[],
  authStatusOptions: AuthStatusOptionModel[],
): UseConfigurationSelectionsReturn => {
  const [selectedAuthStatuses, setSelectedAuthStatuses] = useState<string[]>(
    [],
  );
  const [selectedPayerPatterns, setSelectedPayerPatterns] = useState<string[]>(
    [],
  );

  // Helper function to get auth status selections from config
  const getAuthStatusSelections = useCallback((): string[] => {
    if (!config) return [];
    if (
      Array.isArray(config.auth_status) &&
      config.auth_status.length === 1 &&
      config.auth_status[0] === "any"
    ) {
      return authStatusOptions.length > 0
        ? authStatusOptions.map((option) => option.id)
        : [];
    }
    return Array.isArray(config.auth_status) ? config.auth_status : [];
  }, [config, authStatusOptions]);

  // Helper function to normalize a single stored pattern to match available options
  const normalizePayerPattern = useCallback(
    (storedPattern: string): string | null => {
      // Find matching Insurance Plan (case-insensitive)
      const matchingPlan = payerPatternOptions.find((plan) =>
        comparePayerPatterns(storedPattern, plan),
      );

      // Return the normalized plan if found, otherwise check if storedPattern itself exists
      if (matchingPlan) {
        return matchingPlan;
      }

      // Fallback: if storedPattern is already in options (exact match), use it
      return payerPatternOptions.includes(storedPattern) ? storedPattern : null;
    },
    [payerPatternOptions],
  );

  // Helper function to get payer pattern selections from config
  // Includes normalization logic to match stored patterns with available options
  const getPayerPatternSelections = useCallback((): string[] => {
    if (!config) return [];

    const payerPatterns = config.payer_patterns;
    if (!Array.isArray(payerPatterns)) return [];

    // Handle "any" case - return all available options
    if (payerPatterns.length === 1 && payerPatterns[0] === "any") {
      return [...payerPatternOptions];
    }

    // Early return if no options available for normalization
    if (payerPatternOptions.length === 0) {
      return payerPatterns;
    }

    // Normalize stored patterns: match case-insensitively and filter to only existing options
    const normalizedPatterns = payerPatterns
      .map(normalizePayerPattern)
      .filter((pattern): pattern is string => pattern !== null);

    return normalizedPatterns;
  }, [config, payerPatternOptions, normalizePayerPattern]);

  // Initialize selected values from config when modal opens
  useEffect(() => {
    if (config && isOpen) {
      setSelectedAuthStatuses(getAuthStatusSelections());
      setSelectedPayerPatterns(getPayerPatternSelections());
    }
  }, [config, isOpen, getAuthStatusSelections, getPayerPatternSelections]);

  const handleAuthStatusToggle = createToggleHandler(setSelectedAuthStatuses);

  const handlePayerPatternToggle = createToggleHandler(
    setSelectedPayerPatterns,
  );

  const handleSelectAllAuthStatuses = (allOptions: string[]) => {
    // Always select all (matching worklist filter behavior)
    setSelectedAuthStatuses([...allOptions]);
  };

  const handleClearAllAuthStatuses = () => {
    setSelectedAuthStatuses([]);
  };

  const handleSelectAllPayerPatterns = (allOptions: string[]) => {
    // Always select all (matching worklist filter behavior)
    setSelectedPayerPatterns([...allOptions]);
  };

  const handleClearAllPayerPatterns = () => {
    setSelectedPayerPatterns([]);
  };

  const resetSelections = () => {
    setSelectedAuthStatuses(getAuthStatusSelections());
    setSelectedPayerPatterns(getPayerPatternSelections());
  };

  return {
    selectedAuthStatuses,
    selectedPayerPatterns,
    handleAuthStatusToggle,
    handlePayerPatternToggle,
    handleSelectAllAuthStatuses,
    handleClearAllAuthStatuses,
    handleSelectAllPayerPatterns,
    handleClearAllPayerPatterns,
    resetSelections,
  };
};
